var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Typography } from '@axafr-btc-ds/typography';
import React, { useEffect, useRef } from 'react';
import { autoCompletionButton, autoCompletionContainer, autoCompletionList as autoCompletionListStyle, autoCompletionListItem, autoCompletionListItemActive, autoCompletionListItemLabel, autoCompletionListItemStriped, autoCompletionListItemSubLabel, autoCompletionWrapper, buttonStyle, inputTextContainerWithAutocomplete } from './css';
import { ScrollDirection, maxIndex, useManageAutoComplete } from './hooks/use-manage-auto-complete.hook';
import { InputText } from './input-text';
import { BorderRadius } from '@axafr-btc-ds/layout';
import { cx } from '@emotion/css';
import { v4 as uuidv4 } from 'uuid';
var defaultNumberOfCharactersForAutocomplete = 3;
var defaultNumberOfItems = 5;
var approximateVisibleElements = 2.5;
var elementHeightMultiplier = 1.5;
function shouldScroll(scrollDirection, currentItemIndex, data, limitNumberOfListItem) {
    if (currentItemIndex === null) {
        return false;
    }
    var maxIdx = maxIndex(data, limitNumberOfListItem);
    var isGoingDownOnLastXElements = scrollDirection === ScrollDirection.DOWN
        && currentItemIndex > approximateVisibleElements;
    var isGoingUpOnFirstXElements = scrollDirection === ScrollDirection.UP
        && currentItemIndex < (maxIdx - approximateVisibleElements);
    return isGoingDownOnLastXElements || isGoingUpOnFirstXElements;
}
// eslint-disable-next-line max-params
function scrollIfApplicable(scrollDirection, currentItemIndex, data, limitNumberOfListItem, ulRef) {
    if (!shouldScroll(scrollDirection, currentItemIndex, data, limitNumberOfListItem)) {
        return;
    }
    if (!ulRef.current) {
        return;
    }
    var liRef = ulRef.current.children[currentItemIndex];
    ulRef.current.scrollBy({
        behavior: 'smooth',
        top: scrollDirection * liRef.clientHeight * elementHeightMultiplier
    });
}
function focusBottomButton(btnWrapperRef) {
    var btn = btnWrapperRef.current.children[0];
    if (btn) {
        btn.focus();
    }
}
// eslint-disable-next-line complexity, max-lines-per-function, max-statements
export var InputAutoCompletion = function (_a) {
    var _b = _a.defaultValue, defaultValue = _b === void 0 ? '' : _b, _c = _a.minNumberOfCharactersForAutocomplete, minNumberOfCharactersForAutocomplete = _c === void 0 ? defaultNumberOfCharactersForAutocomplete : _c, isReset = _a.isReset, autoCompletionList = _a.autoCompletionList, bottomButtonClickHandler = _a.bottomButtonClickHandler, _d = _a.bottomButtonLabel, bottomButtonLabel = _d === void 0 ? 'label bouton' : _d, _e = _a.limitNumberOfListItem, limitNumberOfListItem = _e === void 0 ? defaultNumberOfItems : _e, _f = _a.dataNotFoundLabel, dataNotFoundLabel = _f === void 0 ? 'Aucune données trouvées' : _f, _g = _a.borderRadius, borderRadius = _g === void 0 ? BorderRadius.u0 : _g, onItemSelected = _a.onItemSelected, _h = _a.variant, variant = _h === void 0 ? 'striped' : _h, props = __rest(_a, ["defaultValue", "minNumberOfCharactersForAutocomplete", "isReset", "autoCompletionList", "bottomButtonClickHandler", "bottomButtonLabel", "limitNumberOfListItem", "dataNotFoundLabel", "borderRadius", "onItemSelected", "variant"]);
    var btnWrapperRef = useRef();
    var ulRef = useRef();
    var listId = uuidv4();
    var refAutoCompletion = useRef(null);
    var _j = useManageAutoComplete({
        data: autoCompletionList,
        defaultValue: defaultValue,
        limitNumberOfListItem: limitNumberOfListItem,
        onPressEnterOnItem: function (item) { return onItemSelected === null || onItemSelected === void 0 ? void 0 : onItemSelected(item); },
        refAutoCompletion: refAutoCompletion,
        minNumberOfCharactersForAutocomplete: minNumberOfCharactersForAutocomplete,
        bottomButtonClickHandler: bottomButtonClickHandler,
        update: props.onInput || props.onChange
    }), currentItemIndex = _j.currentItemIndex, value = _j.value, setValue = _j.setValue, onSelected = _j.onSelected, onKeyDownHandler = _j.onKeyDownHandler, showAutoCompletion = _j.showAutoCompletion, onBottomButtonArrowUpOrEsc = _j.onBottomButtonArrowUpOrEsc, onBottomButtonClick = _j.onBottomButtonClick, onBottomButtonEsc = _j.onBottomButtonEsc, onInputHandler = _j.onInputHandler, scrollDirection = _j.scrollDirection;
    useEffect(function () {
        setValue(defaultValue);
    }, [isReset, defaultValue]);
    useEffect(function () {
        scrollIfApplicable(scrollDirection, currentItemIndex, autoCompletionList, limitNumberOfListItem, ulRef);
        if (currentItemIndex === -1) {
            focusBottomButton(btnWrapperRef);
        }
    }, [currentItemIndex]);
    var onBottomButtonKeyDown = function (event) {
        if (['ArrowUp', 'Escape'].includes(event.key)) {
            var input = refAutoCompletion.current.querySelector('input');
            if ('focus' in input) {
                input.focus();
                onBottomButtonArrowUpOrEsc();
            }
            if (event.key === 'Escape') {
                onBottomButtonEsc();
            }
        }
    };
    var onItemClick = function (item) {
        onSelected(item);
        onItemSelected === null || onItemSelected === void 0 ? void 0 : onItemSelected(item);
    };
    return (React.createElement("div", { ref: refAutoCompletion, className: autoCompletionWrapper },
        React.createElement(InputText, __assign({}, props, { "aria-expanded": showAutoCompletion, "aria-controls": "dropdown-listbox_".concat(listId), autoComplete: "off", type: "text", value: value, onInput: onInputHandler, parentClassName: cx(inputTextContainerWithAutocomplete(showAutoCompletion, borderRadius), props.parentClassName), onKeyDown: onKeyDownHandler, role: "combobox" }), showAutoCompletion && (React.createElement("div", { className: autoCompletionContainer },
            React.createElement("ul", { "aria-label": "Liste de r\u00E9sultats de recherche", className: autoCompletionListStyle, id: "dropdown-listbox_".concat(listId), ref: ulRef, role: "listbox" },
                autoCompletionList.slice(0, limitNumberOfListItem).map(function (item, index) {
                    var _a;
                    return (React.createElement("li", { key: "".concat(item === null || item === void 0 ? void 0 : item.label, "-").concat(item === null || item === void 0 ? void 0 : item.subLabel, "-").concat(index), onClick: function () { return onItemClick(item); }, className: cx(autoCompletionListItem, index === currentItemIndex ? autoCompletionListItemActive : undefined, variant === 'striped' ? autoCompletionListItemStriped : undefined) },
                        React.createElement(Typography, { variant: "body2", className: autoCompletionListItemLabel, component: "label" }, item === null || item === void 0 ? void 0 : item.label),
                        ((_a = item === null || item === void 0 ? void 0 : item.subLabel) === null || _a === void 0 ? void 0 : _a.length) > 0 && (React.createElement(Typography, { variant: "body3", className: autoCompletionListItemSubLabel, component: "label" }, item === null || item === void 0 ? void 0 : item.subLabel))));
                }),
                (autoCompletionList === null || autoCompletionList === void 0 ? void 0 : autoCompletionList.length) === 0 && (React.createElement("li", { className: autoCompletionListItem },
                    React.createElement(Typography, { variant: "body1", className: autoCompletionListItemLabel, component: "label" }, dataNotFoundLabel)))),
            React.createElement("div", { className: autoCompletionButton, ref: btnWrapperRef },
                React.createElement(Typography, { variant: "body2", bodyFontWeight: "600", component: "button", onClick: onBottomButtonClick, align: "center", onKeyDown: onBottomButtonKeyDown, className: buttonStyle }, bottomButtonLabel)))))));
};
